import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Target, CheckCircle } from 'lucide-react';

export default function Programs() {
  const programs = [
    {
      title: "Beginner Training",
      category: "Fundamentals",
      desc: "Master the basics of grip, stance, and safety protocols for newcomers.",
      image: "/images/beginner.png",
      glow: "group-hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
    },
    {
      title: "Intermediate Training",
      category: "Skill Building",
      desc: "Focus on skill improvement, consistency building, and competitive match preparation.",
      image: "/images/intermediate.png",
      glow: "group-hover:shadow-[0_0_30px_rgba(255,215,0,0.3)]"
    },
    {
      title: "Professional Training",
      category: "Olympic Level",
      desc: "Intense preparation, mental coaching, and technical mastery for aspirants.",
      image: "/images/pro.png",
      glow: "group-hover:shadow-[0_0_30px_rgba(230,0,0,0.3)]"
    },
    {
      title: "Youth Programs",
      category: "Junior Elite",
      desc: "Specialized structural training designed specifically for school students.",
      image: "/images/youth.png",
      glow: "group-hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
    }
  ];

  return (
    <section id="programs" className="relative py-32 bg-black px-6 overflow-hidden">
      
      {/* --- MAIN SECTION BACKGROUND --- */}
      <div className="absolute inset-0 z-0">
        <motion.img 
          src="/images/academy-bg.jpg" 
          alt="Chaitanya Shooting Academy Training Programs Background"
          loading="lazy"
          decoding="async"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="w-full h-full object-cover opacity-30 grayscale hover:grayscale-0 transition-all duration-1000"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-4">
               <Target className="text-red-600" size={16} />
               <p className="text-red-600 font-black tracking-[0.4em] uppercase text-[10px] font-body">Professional Curriculum</p>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black italic uppercase leading-[0.85] text-white tracking-tighter">
              TRAINING <span className="text-gold">PROGRAMS</span>
            </h2>
          </motion.div>
        </div>

        {/* --- PROGRAM CARDS GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {programs.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className={`group relative overflow-hidden rounded-[3rem] bg-zinc-900 border border-white/10 h-[480px] sm:h-[520px] md:h-[550px] cursor-pointer transition-all duration-500 hover:border-white/40 ${p.glow}`}
            >
              {/* --- IMAGE WITH CONTINUOUS ZOOM AND HOVER GLOW --- */}
              <div className="absolute inset-0 z-0 overflow-hidden">
                <motion.img 
                  src={p.image} 
                  alt={p.title} 
                  loading="lazy"
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                  className="w-full h-full object-cover opacity-70 transition-all duration-700 group-hover:opacity-100 group-hover:scale-110 filter brightness-95" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent/20 z-10" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-b from-white/10 to-transparent transition-opacity duration-500 z-15" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-50 bg-red-600/20 transition-opacity duration-500 z-12" />
              </div>

              {/* --- CARD CONTENT --- */}
              <div className="relative z-20 h-full p-8 flex flex-col justify-end">
                <motion.span 
                  className="text-gold font-black text-[9px] uppercase tracking-[0.4em] mb-3 block bg-gold/10 w-fit px-3 py-1 rounded-full border border-gold/20 backdrop-blur-md"
                >
                  {p.category}
                </motion.span>
                
                <h4 className="font-heading text-4xl font-black text-white uppercase italic leading-[0.9] mb-4 tracking-tighter group-hover:text-gold transition-colors duration-300 drop-shadow-lg">
                  {p.title}
                </h4>
                
                <div className="overflow-hidden h-0 group-hover:h-24 transition-all duration-500 ease-in-out">
                  <p className="text-zinc-200 text-sm leading-relaxed mb-6 font-medium">
                    {p.desc}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
                  <span className="text-white font-black text-[10px] uppercase italic tracking-widest opacity-0 group-hover:opacity-100 transition-all duration-500">
                    View Module
                  </span>
                  <div className="w-12 h-12 rounded-2xl border border-white/10 flex items-center justify-center bg-black/50 group-hover:bg-red-600 group-hover:border-red-600 group-hover:rotate-45 transition-all duration-500 shadow-lg">
                    <ChevronRight size={20} className="text-white -rotate-45 group-hover:rotate-0 transition-all duration-500" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}